import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useBoundStore } from '@Store/useBoundStore';
import { buildNavigationStack, HttpService } from '@Services/index';
import { useCountdownTimer } from '@Hooks/useTimer';
import { updateCustomerDataFromServer } from '@Services/init/util';
import { RoutesMap } from '@Router/router.types';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import { OnboardingTypeEnum } from '@Store/index';
import { identifyLogRocketUser } from '@Services/analytics/logrocket.service';
import { useRequestLoginOtp } from './useRequestLoginOtp';
import type { RegionCode } from 'google-libphonenumber';
import type { ServerErrorResponse } from '@Types/index';

type RequestMfaCodeResponse = {
  customerId: string;
  mfaDestination: string;
  mfaType: string;
};

const useVerifyCode = (onError: (error: ServerErrorResponse) => void) => {
  const resetPasswordSlice = useBoundStore((state) => state.resetPasswordSlice);
  const { requestMfaType, customerId, stateToken } = resetPasswordSlice;
  return useMutation({
    mutationFn: async ({ otpAnswer }: { otpAnswer: string }) => {
      const response = await HttpService.post(`v3/onboarding/user/${customerId}/validateMfa`, {
        answer: otpAnswer,
        mfaType: requestMfaType,
        resetPassword: false,
        stateToken: stateToken,
      });
      return response?.data || '';
    },
    onError: (error: ServerErrorResponse) => {
      onError(error);
    },
  });
};

export const useLoginOtpVerificationLogic = () => {
  const { mutateAsync: requestLoginOtp, isPending: isRequestLoginOtpPending } =
    useRequestLoginOtp();
  const [error, setError] = useState<string | undefined>(undefined);
  const { mutateAsync: verifyCode, isPending: isVerifyCodePending } = useVerifyCode((errData) => {
    setError(errData?.response?.data?.errors[0]?.localizedMessage || '');
  });

  const { padTimeLeft, startTimer } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });

  const customerData = useBoundStore((state) => state.getCustomerData());
  const setOnboardingIdentifier = useBoundStore
    .getState()
    .onboardingSlice.getOnboardingData().setOnboardingIdentifier;
  const resetPasswordSlice = useBoundStore((state) => state.resetPasswordSlice);
  const email = resetPasswordSlice.email;
  const mfaType = resetPasswordSlice.requestMfaType;
  const mfaDestination = resetPasswordSlice.mfaDestination;

  const storeResponseData = (responseData: RequestMfaCodeResponse) => {
    resetPasswordSlice.setCustomerId(responseData.customerId);
    resetPasswordSlice.setMfaDestination(responseData.mfaDestination);
  };

  const handleVerifyCode = async (otpAnswer: string) => {
    const response = await verifyCode({ otpAnswer });

    if (
      response?.onboardingTypeEnum &&
      response?.onboardingTypeEnum === OnboardingTypeEnum.MULTI_BUSINESS
    ) {
      setOnboardingIdentifier(OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS);
    }

    const customerDataToUpdate = { ...customerData };
    customerDataToUpdate.externalId = response.externalId;
    customerDataToUpdate.customerId = response.externalId;
    customerDataToUpdate.accessToken = response.accessToken;
    customerDataToUpdate.onboardingStatus = response.onboardingStatus;
    if (response?.onboardingTypeEnum) {
      customerDataToUpdate.customerData.onboardingTypeEnum = response.onboardingTypeEnum;
    }

    customerDataToUpdate.nameTooLong = response.nameTooLong;
    customerDataToUpdate.allowEditingPersonalDetails = response.allowEditingPersonalDetails;
    customerDataToUpdate.hasSoleProprietor = response.hasSoleProprietor;
    customerDataToUpdate.availablePromoCodes = response.availablePromoCodes;
    customerDataToUpdate.bank = response.bank;
    customerDataToUpdate.isNonUsSupported = response.isNonUsSupported;
    customerDataToUpdate.idCountry = response.idCountry;
    customerDataToUpdate.nationalIdDescription = response.nationalIdDescription;
    customerDataToUpdate.campaignName = response.campaignName;
    customerDataToUpdate.customerData = {
      ...customerDataToUpdate.customerData,
      ...response.customerData,
    };

    customerDataToUpdate.customerData.lastStepData = response
      ?.customerOnboardingLastStepDataResponse?.stepName
      ? {
          version: Number(response?.customerOnboardingLastStepDataResponse?.version || 0),
          stepName: response?.customerOnboardingLastStepDataResponse?.stepName || '',
        }
      : {
          version: 999,
          stepName: RoutesMap.PERSONAL_DETAILS,
        };
    customerDataToUpdate.customerData.phoneCountryCode = response.phoneCountryCode as RegionCode;

    updateCustomerDataFromServer(customerDataToUpdate, customerData);
    identifyLogRocketUser(response.externalId, {
      isReturningUser: true,
      hasMfa: true,
      onboardingStatus: response.onboardingStatus,
      onboardingTypeEnum: response.onboardingTypeEnum,
    });
    buildNavigationStack({ shouldHandleLoginMannually: true });
  };

  const requestMfaCode = async () => {
    try {
      const responseData = await requestLoginOtp({ loginName: email, mfaType: mfaType });
      storeResponseData(responseData);
      startTimer();
    } catch (requestOtpError) {
      console.error('Error requesting MFA code:', requestOtpError);
      setError('Failed to request verification code. Please try again.');
    }
  };

  const clearError = () => {
    setError(undefined);
  };

  useEffect(() => {
    requestLoginOtp({ loginName: email, mfaType: mfaType }).then((responseData) => {
      storeResponseData(responseData);
    });
  }, []);

  return {
    isPending: isVerifyCodePending || isRequestLoginOtpPending,
    requestMfaCode,
    handleVerifyCode,
    padTimeLeft,
    error,
    clearError,
    mfaDestination,
  };
};
